import React, { useState } from 'react';

import { Controller, useForm } from 'react-hook-form';

import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import { queueItemsPurchasedSummary } from '~/services/ReportingService';
import { addMonths, formatDateISO, startOfMonth } from '~/services/TimeService';

import ErrorDisplay from '../ErrorDisplay';

enum OrderType {
  STANDARD = 'standard',
  WITH_TEST = 'with_test',
  WITH_TEST_AND_FAILED = 'with_test_and_failed',
}
interface FormData {
  startAt: string;
  endAt: string;
  orderType: OrderType;
}

export default function ExportItemsPurchasedSummaryForm() {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      startAt: formatDateISO(addMonths(-1, startOfMonth())),
      endAt: formatDateISO(addMonths(0, startOfMonth())),
      orderType: OrderType.STANDARD,
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      setError(null);
      setShowSuccessMessage(false);

      await queueItemsPurchasedSummary({
        startAt: data.startAt,
        endAt: data.endAt,
        orderType: data.orderType,
      });

      setShowSuccessMessage(true);
    } catch (err: unknown) {
      setError((err as Error).message || 'An error occurred while queuing the report');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ maxWidth: 600 }}>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Please enter a date to export the items purchased summary between the selected date range
      </Typography>

      {error && (
        <Box sx={{ mb: 3 }}>
          <ErrorDisplay message={error} />
        </Box>
      )}

      {showSuccessMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography>Your report is queued for delivery and will be emailed shortly.</Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Controller
          name="startAt"
          control={control}
          rules={{ required: 'Please enter start date' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Select Start Date"
              type="date"
              fullWidth
              error={!!error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name="endAt"
          control={control}
          rules={{ required: 'Please enter end date' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Select End Date"
              type="date"
              fullWidth
              error={!!error}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          name="orderType"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error}>
              <FormLabel component="legend">Order Type</FormLabel>
              <RadioGroup {...field} row sx={{ mt: 1 }}>
                <FormControlLabel value={OrderType.STANDARD} control={<Radio />} label="Standard" />
                <FormControlLabel value={OrderType.WITH_TEST} control={<Radio />} label="With Test Orders" />
                <FormControlLabel
                  value={OrderType.WITH_TEST_AND_FAILED}
                  control={<Radio />}
                  label="With Test and Failed Orders"
                />
              </RadioGroup>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          )}
        />

        <Button type="submit" variant="contained" disabled={isSubmitting} sx={{ alignSelf: 'flex-start' }}>
          {isSubmitting ? 'Queueing Report...' : 'Email Items Purchased Summary Report'}
        </Button>
      </Box>
    </Box>
  );
}
